import Breadcrumbs from 'components/Common/Breadcrumb';
import Loader from 'components/Loader';
import { useFormik } from 'formik';
import useRequest from 'hooks/useRequest';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { achievementsLevel, achievementsType } from 'utils/constants';
import { titleCaseIfExists } from 'utils/helper';
import { SMKLinks } from 'utils/links';
import * as Yup from 'yup';

const AddUpdateAchievements = () => {
  const history = useHistory();
  const { id: achievementID } = useParams();

  toast.configure();

  const [achievementDetails, setAchievementDetails] = useState();

  const ACHIEVEMENT_TYPE = Object.keys(achievementsType)?.map((e) => ({
    label: titleCaseIfExists(e),
    value: achievementsType[e],
  }));
  const ACHIEVEMENT_LEVEL = Object.keys(achievementsLevel)?.map((e) => ({
    label: titleCaseIfExists(e),
    value: achievementsLevel[e],
  }));

  const [getAchievementRequest, { loading: getAchievementLoading }] = useRequest({
    method: 'GET',
    url: `/achievement/admin/${achievementID}`,
    onSuccess: (data) => {
      setAchievementDetails(data);
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [addAchievementRequest, { loading: addAchievementLoading }] = useRequest({
    method: 'POST',
    url: `/achievement/admin`,
    onSuccess: (data) => {
      const url = SMKLinks.ACHIEVEMENT_LISTING;
      history.replace(url);
      toast.success('Success! Achievement has been added.');
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const [updateAchievementRequest, { loading: updateAchievementLoading }] = useRequest({
    method: 'PUT',
    url: `/achievement/admin/${achievementID}`,
    onSuccess: () => {
      toast.success('Success! Achievement has been updated.');
      window.location.reload();
    },
    onError: (err) => {
      toast.error(err?.message?.message || err?.message);
    },
  });

  const isEmptyValue = (val) => (val !== null || val !== undefined ? val : undefined);

  const editInitialValues = () => {
    return {
      active: achievementDetails?.active,
      deleted: achievementDetails?.deleted,
      name: achievementDetails?.name,
      // slug: achievementDetails?.slug,
      type: !isEmpty(achievementDetails?.type)
        ? ACHIEVEMENT_TYPE?.find((e) => e?.value === achievementDetails?.type)
        : null,
      level: !isEmpty(achievementDetails?.level)
        ? ACHIEVEMENT_LEVEL?.find((e) => e?.value === achievementDetails?.level)
        : null,
      module: achievementDetails?.module,
      badge: achievementDetails?.badge,
      streak: achievementDetails?.streak || 0,
      uniqueType: achievementDetails?.uniqueType || 0,
    };
  };

  const getInitialValues = () => ({
    active: true,
    deleted: false,
    name: '',
    // slug: '',
    type: null,
    level: null,
    module: '',
    badge: '',
    streak: 0,
    uniqueType: 0,
    // language: LANGUAGE_ARRAY_WITH_STRING[0],
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: isEmpty(achievementID) ? { ...getInitialValues() } : { ...editInitialValues() },
    onSubmit: async (values) => {
      const data = {
        active: values?.active,
        deleted: values?.deleted,
        name: values?.name,
        // slug: values?.slug,
        type: isEmptyValue(values?.type?.value),
        level: isEmptyValue(values?.level?.value),
        module: isEmptyValue(values?.module),
        badge: isEmptyValue(values?.badge),
        streak: values?.streak || 0,
        uniqueType: values?.uniqueType || 0,
        // language: values?.language?.value || 0,
      };

      const payload = {
        body: data,
      };

      if (isEmpty(achievementID)) {
        addAchievementRequest(payload);
      } else {
        updateAchievementRequest(payload);
      }
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Please provide name'),
      // slug: Yup.string().required('Please provide slug'),
      module: Yup.string().required('Please provide module'),
      badge: Yup.string().required('Please provide badge'),

      streak: Yup.number().min(0, 'Please enter a number greater than or equal to 0'),
      uniqueType: Yup.number().min(0, 'Please enter a number greater than or equal to 0'),

      type: Yup.object()
        .required('Please provide type')
        .nullable(true),
      level: Yup.object()
        .required('Please provide level')
        .nullable(true),
    }),
  });

  useEffect(() => {
    if (!isEmpty(achievementID)) {
      getAchievementRequest();
    }
  }, [achievementID]);

  return (
    <div className="page-content">
      <Loader isActive={addAchievementLoading || getAchievementLoading || updateAchievementLoading} />
      <Container fluid>
        <Breadcrumbs title="Achievement" breadcrumbItem={`${isEmpty(achievementID) ? 'Add' : 'Edit'}  Achievement`} />
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={9}>
              <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={12}>
                      <FormGroup className="mb-4">
                        <Label for="name" className="form-label  ">
                          Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          className="form-control"
                          placeholder="Enter Name..."
                          invalid={!!(formik.touched.name && formik.errors.name)}
                          {...formik.getFieldProps('name')}
                        />
                        <FormFeedback className="d-block">{formik.errors.name}</FormFeedback>
                      </FormGroup>
                    </Col>

                    {/* <Col md={12} className="mb-4">
                      <SlugInput
                        formik={formik}
                        ID={achievementID}
                        nameValue={formik.values.name}
                        formGroupClassName="mb-0"
                      />
                    </Col> */}

                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="type" className="form-label">
                          Type
                        </Label>
                        <Select
                          id="type"
                          options={ACHIEVEMENT_TYPE}
                          value={formik.values.type}
                          onChange={(type) => formik.setFieldValue('type', type)}
                        />
                        <FormFeedback className="d-block">{formik.errors.type}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="level" className="form-label">
                          Level
                        </Label>
                        <Select
                          id="level"
                          options={ACHIEVEMENT_LEVEL}
                          value={formik.values.level}
                          onChange={(level) => formik.setFieldValue('level', level)}
                        />
                        <FormFeedback className="d-block">{formik.errors.level}</FormFeedback>
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="module" className="form-label  ">
                          Module <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="module"
                          name="module"
                          type="text"
                          className="form-control"
                          placeholder="Enter Module..."
                          invalid={!!(formik.touched.module && formik.errors.module)}
                          {...formik.getFieldProps('module')}
                        />
                        <FormFeedback className="d-block">{formik.errors.module}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="badge" className="form-label  ">
                          Badge <span className="text-danger">*</span>
                        </Label>
                        <Input
                          id="badge"
                          name="badge"
                          type="text"
                          className="form-control"
                          placeholder="Enter Badge..."
                          invalid={!!(formik.touched.badge && formik.errors.badge)}
                          {...formik.getFieldProps('badge')}
                        />
                        <FormFeedback className="d-block">{formik.errors.badge}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="streak" className="form-label  ">
                          Streak
                        </Label>
                        <Input
                          id="streak"
                          name="streak"
                          type="number"
                          min={0}
                          className="form-control"
                          placeholder="Enter Module..."
                          invalid={!!(formik.touched.streak && formik.errors.streak)}
                          {...formik.getFieldProps('streak')}
                        />
                        <FormFeedback className="d-block">{formik.errors.streak}</FormFeedback>
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup className="mb-4">
                        <Label for="uniqueType" className="form-label  ">
                          Unique Type
                        </Label>
                        <Input
                          id="uniqueType"
                          name="uniqueType"
                          type="number"
                          min={0}
                          className="form-control"
                          placeholder="Enter Unique Type..."
                          invalid={!!(formik.touched.uniqueType && formik.errors.uniqueType)}
                          {...formik.getFieldProps('uniqueType')}
                        />
                        <FormFeedback className="d-block">{formik.errors.uniqueType}</FormFeedback>
                      </FormGroup>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card>
                <CardBody>
                  <div>
                    <Button type="submit" color="primary" className="mb-3 w-100">
                      {achievementID ? 'Save Achievement' : 'Add Achievement'}
                    </Button>

                    <Button
                      className="w-100"
                      color="light"
                      onClick={() => {
                        history.replace(SMKLinks.ACHIEVEMENT_LISTING);
                      }}>
                      Cancel
                    </Button>
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  <Row className="align-items-center">
                    <Col md={12}>
                      <CardTitle className="mb-3">Active </CardTitle>
                      {[true, false].map((option, i) => (
                        <div key={`active_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`active_${option}`}
                            name="active"
                            className="form-check-input"
                            checked={formik.values.active === option}
                            onChange={() => formik.setFieldValue('active', option)}
                          />
                          <label className="form-check-label" htmlFor={`active_${option}`}>
                            {option ? 'Yes' : 'No'}
                          </label>
                        </div>
                      ))}
                    </Col>

                    <Col md={12}>
                      <CardTitle className="mb-3">Delete </CardTitle>
                      {[true, false].map((option, i) => (
                        <div key={`deleted_${i}`} className="form-check mb-3 d-inline-block me-4">
                          <input
                            type="radio"
                            id={`deleted_${option}`}
                            name="deleted"
                            className="form-check-input"
                            checked={formik.values.deleted === option}
                            onChange={() => formik.setFieldValue('deleted', option)}
                          />
                          <label className="form-check-label" htmlFor={`deleted_${option}`}>
                            {option ? 'Yes' : 'No'}
                          </label>
                        </div>
                      ))}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default AddUpdateAchievements;
